import React, { useContext, useEffect, useState } from 'react';
import { Box, Datalist, Link, Sprite } from '@ktag/styleguide/dist/react';
import { Trans, useTranslation } from 'react-i18next';
import useAppState from '../../../AppStore';
import './landowner.scss';
import useLandowners, {
  Eigentuemer,
  Result,
  useRemainingRequests,
} from '../api/useLandowners';
import LandownerGrundstueck from './LandownerGrundstueck';
import LandownerEigentuemer from './LandownerEigentuemer';
import { LandownerNavigation } from '../api/contract';
import IdentifyMessageBox from './IdentifyMessageBox';
import { AuthenticatedUserRequired } from '../../../utils/auth/AuthenticatedUserRequired';
import { useTracking } from '../../../utils/tracking/TrackingContext';
import ToolDialogBackContext from '../../../components/tooldialog/ToolDialogBackContext';

const LandownerResults = () => {
  const { setBackFn } = useContext(ToolDialogBackContext);
  const { t } = useTranslation('agisviewer');
  const { Tracking } = useTracking();
  const identifyDefaultResultProperties = useAppState(
    (state) => state.defaultResultProperties,
  );
  const setActiveFlyinView = useAppState((state) => state.setActiveFlyinView);
  const isLoggedIn = useAppState((state) => state.isLoggedIn);

  if (!identifyDefaultResultProperties?.parcelEgrid)
    return (
      <IdentifyMessageBox message={t('landowner_errorNoEgrid_message')} type="negative" />
    );

  const [landownerNavigation, setLandownerNavigation] = useState<LandownerNavigation[]>([
    {
      level: 0,
      egrid: identifyDefaultResultProperties?.parcelEgrid,
      landownerResponse: null,
      text: '',
    },
  ]);

  const handleBackLinkClick = () => {
    setLandownerNavigation(landownerNavigation.slice(0, -1));
  };

  const handleDownLinkClick = () => {
    setLandownerNavigation([...landownerNavigation]);
  };

  const landownerItems = (results: Result[]) => {
    if (results.length === 0) {
      return (
        <IdentifyMessageBox
          message={t('landowner_errorEmptyLandownerResponse_message')}
          type="information"
        />
      );
    }

    return results.map((result: any) => {
      return result.eigentuemer.map((eigentuemer: Eigentuemer, index: number) => {
        if (eigentuemer.isGemeinschaft === true) {
          const gemeinschaft: Result[] = [
            {
              eigentum: {
                bezeichnung: `${eigentuemer.art} ${eigentuemer.name}`,
                anteilZaehler: '1',
                anteilNenner: '1',
                art: null,
                zusatz: null,
                gemeinde: 'test',
                grundstuecknummer: 1234,
              },
              eigentuemer: eigentuemer.eigentuemer,
            },
          ];
          return landownerItems(gemeinschaft);
        }
        switch (eigentuemer.art) {
          case 'natuerlich':
          case 'juristisch':
            return (
              <LandownerEigentuemer
                eigentum={result.eigentum}
                eigentuemer={eigentuemer}
                isFirstItem={index === 0}
              />
            );
          case 'Grundstück':
            return (
              <LandownerGrundstueck
                result={result}
                landownerNavigation={landownerNavigation}
                linkClicked={handleDownLinkClick}
              />
            );
          default:
            return null;
        }
      });
    });
  };

  useEffect(() => {
    if (!isLoggedIn) {
      setActiveFlyinView('identify'); // falls nicht angemeldet zurück zu Identify
    }
  }, [isLoggedIn]);

  const landownerNavigationItem = landownerNavigation.at(-1);

  if (!landownerNavigationItem) {
    return (
      <IdentifyMessageBox message={t('landowner_errorDefault_message')} type="negative" />
    );
  }

  const { data, isLoading, error, isError } = useLandowners(
    landownerNavigationItem.egrid,
  );

  const { data: remainingRequests } = useRemainingRequests();

  useEffect(() => {
    if (isError) {
      Tracking.Error.landownerRequestError(landownerNavigationItem.egrid, error.message);
    }
  }, [isError]);

  useEffect(() => {
    if (landownerNavigationItem.level > 0) {
      setBackFn(() => handleBackLinkClick);
    } else {
      setBackFn(null);
    }
  }, [setBackFn, landownerNavigationItem]);

  if (error) {
    const link = (
      <Link
        openNewWindow
        title={t('my_account')}
        url="https://www.ag.ch/geoportal/apps/agisviewer/register-phone"
      />
    );

    const errorMessage = (
      <Trans ns="agisviewer" i18nKey={error.message} components={[link]} />
    );

    return <IdentifyMessageBox message={errorMessage} type="negative" />;
  }

  if (isLoading) {
    return (
      <div className="placeholder__top-space">
        <div className="text__placeholder--big placeholder__bottom-space shimmer" />
        {Array.from({ length: 10 }, (_, i) => (
          <div
            key={`esri-layer-setting${i}`}
            className="esri-layer-settings__placeholder"
          >
            <div className="square__placeholder shimmer" />
            <div className="text__placeholder--medium shimmer" />
          </div>
        ))}
      </div>
    );
  }

  if (!data) {
    return (
      <IdentifyMessageBox message={t('landowner_errorDefault_message')} type="negative" />
    );
  }

  const standort = [
    {
      key: `${t('gg25')}:`,
      value: identifyDefaultResultProperties.municipality,
    },
    {
      key: `${t('parcel')}:`,
      value: identifyDefaultResultProperties.parcel,
    },
  ];
  return (
    <>
      <p>
        <Sprite id="i-circle" additionalClassNames="legend__icon" />{' '}
        {(remainingRequests ?? 0) > 10
          ? t('landowner_infotext_unlimited')
          : t('landowner_infotext')}
      </p>
      <p>
        <Box type="highlight">
          <Datalist items={standort} smallkeys />
        </Box>
      </p>
      {landownerNavigationItem.level > 0 && (
        <Link
          icon={{
            id: 'chevron-left',
            title: t('landowner_navigation_back_title'),
          }}
          text={landownerNavigationItem.text}
          title={t('landowner_navigation_back_title')}
          onClickMethod={handleBackLinkClick}
        />
      )}
      <AuthenticatedUserRequired>
        <div className="landowner__results">{landownerItems(data.results)}</div>
      </AuthenticatedUserRequired>
    </>
  );
};

export default LandownerResults;
